// The Facts tab states each set member once (#11426).
//
// Claude Design ruling: "Extend the rule. The edit surface does not show
// storage." A hierarchical array fact is a SET (register rule 24: a surface
// renders its field's meaning, not its storage), so a path an import stored
// twice renders once — here and in the row's editor, whose values seed from
// the rendered fact. A non-hierarchical array is a multiset — two identical
// payment amounts are two payments — and keeps every occurrence.
//
// The ruling's condition — an edit applies to every stored occurrence — holds
// by construction: the save path replaces the whole fact value, so a save
// seeded from the de-duped array rewrites (or removes) all copies at once.
// The OCC baseline must still be built from the PRISTINE fact (see
// buildBaselineFromFact in FactsTab), or every save of a duplicated fact
// would 409 against the stored value.
//
// Reference-preserving: a fact without duplicates — and a list whose facts
// all pass through — comes back unchanged, so memoized consumers and editor
// render keys stay stable.

import type { ArrayFact, ScalarFact } from "../types";
import { isArrayFact } from "../types";

const valueKey = (value: ArrayFact["array"][number]): string =>
  JSON.stringify([value.scalar, value.entity?.id ?? null]);

export function dedupeSetFact<T extends ScalarFact | ArrayFact>(fact: T): T {
  if (!isArrayFact(fact) || !fact.is_hierarchical) return fact;
  const seen = new Set<string>();
  const deduped = fact.array.filter((value) => {
    const key = valueKey(value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (deduped.length === fact.array.length) return fact;
  return { ...fact, array: deduped };
}

export function dedupeSetFacts<T extends ScalarFact | ArrayFact>(
  facts: T[]
): T[] {
  let changed = false;
  const out = facts.map((fact) => {
    const deduped = dedupeSetFact(fact);
    if (deduped !== fact) changed = true;
    return deduped;
  });
  return changed ? out : facts;
}
